/**
 * The bell and its panel. Server-backed (`lib/api/notifications`): the badge
 * polls the unread count, the feed loads when the panel opens, and read state
 * follows the person across devices. The panel is an L3 glass surface: beside
 * the rail on desktop, under the bell on the top bar.
 *
 * Panel: All / Unread tabs, the feed grouped Today / Yesterday / Earlier,
 * "Mark all read", and "Show older" paging. Clicking a row marks it read and
 * opens where it points.
 */

import {
  Bell,
  BookCheck,
  Briefcase,
  CalendarClock,
  ClipboardCheck,
  ClipboardPen,
  FileText,
  FilePlus2,
  Inbox,
  MessageCircle,
  NotebookPen,
  ShieldAlert,
  Video,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useMarkNotificationsRead,
  useNotificationFeed,
  useUnreadCount,
  type AppNotification,
  type NotificationKind,
} from "@/lib/api/notifications";
import { dayKeyOf, timeAgo, todayKey, addDays } from "@/lib/format/time";
import { MenuPopover } from "@/primitives/overlays";
import { Skeleton, SkeletonLine } from "@/primitives/ui/Skeleton";

const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  lesson_reminder: Video,
  wrapup_published: NotebookPen,
  report_published: FileText,
  ai_concern: ShieldAlert,
  wrapup_due: ClipboardPen,
  homework_set: FilePlus2,
  homework_due: CalendarClock,
  homework_submitted: ClipboardCheck,
  homework_graded: BookCheck,
  booking_new: Inbox,
  application_new: Briefcase,
  message: MessageCircle,
};

type Tab = "all" | "unread";

function groupByDay(items: AppNotification[]): Array<{ label: string; items: AppNotification[] }> {
  const today = todayKey();
  const yesterday = addDays(today, -1);
  const groups: Array<{ label: string; items: AppNotification[] }> = [];
  for (const n of items) {
    const key = dayKeyOf(n.created_at);
    const label = key === today ? "Today" : key === yesterday ? "Yesterday" : "Earlier";
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(n);
    else groups.push({ label, items: [n] });
  }
  return groups;
}

export function NotificationsButton({ variant }: { variant: "side" | "bar" }) {
  const navigate = useNavigate();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("all");

  const unreadQuery = useUnreadCount();
  const unread = unreadQuery.data ?? 0;
  const feed = useNotificationFeed(tab === "unread", open);
  const markRead = useMarkNotificationsRead();

  const items = useMemo(() => feed.data?.pages.flatMap((p) => p.results) ?? [], [feed.data]);
  const groups = useMemo(() => groupByDay(items), [items]);

  const badge =
    unread > 0 ? (
      <span className="fr-badge" aria-hidden>
        {unread > 9 ? "9+" : unread}
      </span>
    ) : null;

  const label = unread > 0 ? `Notifications, ${unread} unread` : "Notifications";

  const openItem = (n: AppNotification) => {
    if (n.unread) markRead.mutate([n.id]);
    setOpen(false);
    if (n.url) navigate(n.url);
  };

  return (
    <>
      {variant === "side" ? (
        <button
          ref={ref}
          type="button"
          className="fr-item"
          data-active={open || undefined}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="fr-item__icon">
            <Bell size={18} strokeWidth={1.6} />
          </span>
          <span className="fr-item__label">Notifications</span>
          {unread > 0 ? (
            <span className="fr-item__trail" aria-hidden>
              <span className="fr-count">{unread > 99 ? "99+" : unread}</span>
            </span>
          ) : null}
        </button>
      ) : (
        <button
          ref={ref}
          type="button"
          className="fr-icon-btn"
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <Bell size={20} strokeWidth={1.6} />
          {badge}
        </button>
      )}

      <MenuPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={ref}
        role="dialog"
        align="end"
        minWidth={360}
        placement={variant === "side" ? "right" : "below"}
        className="fr-notes"
        style={{ padding: 0 }}
      >
        <div className="fr-notes__head">
          <h2 className="st-title" style={{ fontSize: 16 }}>
            Notifications
          </h2>
          {unread > 0 ? (
            <button type="button" className="st-quiet" onClick={() => markRead.mutate(undefined)}>
              Mark all read
            </button>
          ) : null}
        </div>
        <div className="fr-notes__tabs" role="tablist" aria-label="Show">
          {(["all", "unread"] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className="fr-notes__tab"
              onClick={() => setTab(t)}
            >
              {t === "all" ? "All" : "Unread"}
              {t === "unread" && unread > 0 ? <span className="fr-notes__tab-count">{unread > 99 ? "99+" : unread}</span> : null}
            </button>
          ))}
        </div>
        <div className="fr-notes__list">
          {feed.isLoading ? (
            <FeedSkeleton />
          ) : items.length === 0 ? (
            <div className="st-note" data-center="true" style={{ paddingBlock: 28 }}>
              <p className="st-note__title">
                {feed.isError ? "Notifications could not load." : tab === "unread" ? "Nothing unread." : "You are all caught up."}
              </p>
              <p className="st-note__body">
                {feed.isError
                  ? "They will retry on their own shortly."
                  : "Lessons, homework and wrap-ups that need you will land here."}
              </p>
              {feed.isError ? (
                <button type="button" className="st-quiet" onClick={() => void feed.refetch()}>
                  Try again
                </button>
              ) : null}
            </div>
          ) : (
            <>
              {groups.map((g) => (
                <div key={g.label} className="fr-notes__group" role="group" aria-label={g.label}>
                  <p className="fr-notes__day">{g.label}</p>
                  {g.items.map((n) => {
                    const Icon = KIND_ICON[n.kind] ?? Bell;
                    return (
                      <button
                        key={n.id}
                        type="button"
                        className="fr-note"
                        data-unread={n.unread || undefined}
                        data-kind={n.kind}
                        onClick={() => openItem(n)}
                      >
                        <span className="fr-note__icon" aria-hidden>
                          <Icon size={15} strokeWidth={1.75} />
                        </span>
                        <span className="fr-note__text">
                          <strong>{n.title}</strong> {n.body}
                          <span className="fr-note__time">{timeAgo(n.created_at)}</span>
                        </span>
                        {n.unread ? <span className="fr-note__unread" aria-label="Unread" /> : null}
                      </button>
                    );
                  })}
                </div>
              ))}
              {feed.hasNextPage ? (
                <div className="fr-notes__more">
                  <button
                    type="button"
                    className="st-quiet"
                    onClick={() => void feed.fetchNextPage()}
                    disabled={feed.isFetchingNextPage}
                  >
                    {feed.isFetchingNextPage ? "Loading..." : "Show older"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </MenuPopover>
    </>
  );
}

/* Two day groups, three and four rows, alternating one and two text lines,
   enough to fill the list's 460px. */
const SKELETON_GROUPS = [
  [2, 1, 2],
  [1, 2, 1, 2],
];

/**
 * The feed while it loads, in the feed's own classes: each day label (10.5px
 * line) and `fr-note` rows with the 32px icon, the 13px x 1.45 text lines and
 * the time line 4px under them.
 */
function FeedSkeleton() {
  return (
    <div aria-hidden>
      {SKELETON_GROUPS.map((rows, g) => (
        <div key={g} className="fr-notes__group">
          <div className="fr-notes__day">
            <SkeletonLine lineHeight={10.5} barHeight={8} width={g === 0 ? 40 : 56} />
          </div>
          {rows.map((lines, i) => (
            <div key={i} className="fr-note">
              <Skeleton block width={32} height={32} radius="var(--radius-pill)" />
              <span className="fr-note__text">
                <SkeletonLine
                  lineHeight="calc(13px * 1.45)"
                  barHeight={10}
                  lines={lines}
                  width={lines > 1 ? "100%" : `${78 - i * 8}%`}
                  lastWidth="52%"
                />
                <span className="fr-note__time">
                  <SkeletonLine lineHeight={10.5} barHeight={8} width={44} />
                </span>
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
